<template>
    <div v-if="loading" class="ma-5 text-center">
        <KpIcon icon-cls="load ic-spin" color="grey" />
    </div>
    <div v-else-if="items.length" class="content-container">
        <v-item-group v-model="selectedId" class="item-container" style="grid-area: items">
            <v-item v-for="item in items" :key="item.id" v-slot="{ active, toggle }" :value="item.id">
                <ImageCard
                    :active="active"
                    :image="item"
                    :checked="checkedItems.includes(item.getId())"
                    :hide-multiselect="hideMultiselect"
                    @checked="onImageCheckChange(item, $event)"
                    @delete="onDeleteBtn"
                    @duplicate="$emit('duplicate', $event)"
                    @click="toggleOrDblClick(toggle, item)"
                />
            </v-item>
        </v-item-group>
        <div style="grid-area: paging">
            <v-pagination
                :value="page"
                :length="pagerLength"
                :total-visible="pagerTotalVisible"
                @input="$emit('page', $event)"
            ></v-pagination>
        </div>
        <ConfirmDialog
            v-model="showConfirmDeleteDlg"
            :context="imageToDelete ? imageToDelete.title : null"
            @confirm="onDeleteYes"
            @cancel="onDeleteNo"
        />
    </div>
    <div v-else>
        {{ $t('no_recs_found') }}
    </div>
</template>

<script>
import { Image } from '@/models/Image';
import ImageCard from './ImageCard.vue';

export default {
    name: 'MediaManagerContentView',
    components: { ImageCard },
    props: {
        /** array of items (now: images) */
        items: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        /** if true, the image's multiselect functions are not shown */
        hideMultiselect: {
            type: Boolean,
            default: false,
        },
        initialSelected: {
            type: Image,
            default: null,
        },
        initialChecked: {
            type: Array,
            default: () => [],
        },

        page: {
            type: Number,
            required: true,
        },
        pageSize: {
            type: Number,
            required: true,
        },
        pagerTotalVisible: {
            type: Number,
            default: 6,
        },
        totalItems: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            selectedId: this.initialSelected?.id,
            showConfirmDeleteDlg: false,
            imageToDelete: null,
            checkedItems: this.initialChecked,
        };
    },
    computed: {
        selected() {
            for (const item of this.items) {
                if (item.id === this.selectedId) {
                    return item;
                }
            }
            return null;
        },
        pagerLength() {
            return Math.ceil(this.totalItems / this.pageSize);
        },
    },
    watch: {
        selected(act) {
            this.$emit('change', act);
        },
        checkedItems(value) {
            this.$emit('checked-items', value);
        },
        initialChecked(value) {
            this.checkedItems = value;
        },
    },

    methods: {
        toggleOrDblClick(toggleFn, item) {
            let now = new Date().getTime();
            if (this._lastClick && now - this._lastClick < 250) {
                // dbl click detected
                this.$emit('dblclick', item);
            } else {
                // toggle, if another item is selected:
                if (this.selected !== item) {
                    toggleFn();
                }
            }
            this._lastClick = now;
        },

        onDeleteBtn(imgRec) {
            this.imageToDelete = imgRec;
            this.showConfirmDeleteDlg = true;
        },

        onDeleteYes() {
            if (this.imageToDelete) {
                this.$emit('delete', this.imageToDelete);
            }
        },

        onDeleteNo() {
            this.imageToDelete = null;
            this.showConfirmDeleteDlg = false;
        },

        onImageCheckChange(image, value) {
            if (value) {
                this.checkedItems = [...this.checkedItems, ...[image.getId()]];
            } else {
                this.checkedItems = [...this.checkedItems.filter((i) => i !== image.getId())];
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.content-container {
    display: grid;
    height: 100%;
    overflow: hidden;
    grid-template-rows: 1fr auto;
    grid-template-areas:
        'items'
        'paging';
}
.item-container {
    display: flex;
    overflow: auto;
    flex-wrap: wrap;
    gap: 5px;
}
</style>
